import { useEffect, useState } from "react";
import { Link } from "react-router";
import { ArrowLeft, Check, ClipboardList, Eye, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { resolverConfiguracaoDePacientes, type ConfiguracaoDePacientes } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useSetPageHeader } from "@/lib/page-header";
import { useIsMobile } from "@/lib/use-media-query";
import { useOcupaRodape } from "@/components/ajuda/RodapeOcupado";
import { SecaoCadastro } from "@/components/settings/pacientes/SecaoCadastro";
import { SecaoAnamnese } from "@/components/settings/pacientes/SecaoAnamnese";
import { SecaoFicha } from "@/components/settings/pacientes/SecaoFicha";

type SectionKey = "cadastro" | "anamnese" | "ficha";

type Section = {
  key: SectionKey;
  label: string;
  description: string;
  icon: React.ReactNode;
  accent: string;
};

const SECTIONS: Section[] = [
  { key: "cadastro", label: "Cadastro", description: "Campos opcionais, obrigatórios ou ocultos", icon: <UserPlus className="h-4 w-4" />, accent: "#2457ff" },
  { key: "anamnese", label: "Anamnese", description: "Link, mensagem e renovação", icon: <ClipboardList className="h-4 w-4" />, accent: "#0ea5e9" },
  { key: "ficha", label: "Visualização da ficha", description: "Abas que aparecem na ficha", icon: <Eye className="h-4 w-4" />, accent: "#10b981" },
];

const PADRAO = resolverConfiguracaoDePacientes(null);
const igualAoPadrao = (v: ConfiguracaoDePacientes) => JSON.stringify(v) === JSON.stringify(PADRAO);

/** `embutido`: dentro de uma seção de outra página (Configurações, Minha conta) — sem título
 *  próprio, sem "voltar" e com as subseções em abas, já que a de fora tem a barra lateral. */
export function SettingsPatients({ embutido = false }: { embutido?: boolean } = {}) {
  const ocupaRodape = useOcupaRodape<HTMLDivElement>();
  const compacto = useIsMobile() || embutido;
  useSetPageHeader({
    title: "Configurações · Pacientes",
    subtitle: "Personalize o fluxo do cadastro até a ficha",
  }, [], !embutido);

  const [section, setSection] = useState<SectionKey>("cadastro");
  const active = SECTIONS.find((s) => s.key === section)!;
  const utils = trpc.useUtils();
  const query = trpc.settings.getPacientes.useQuery();
  const [valor, setValor] = useState<ConfiguracaoDePacientes>(PADRAO);
  useEffect(() => {
    if (query.data) setValor(query.data);
  }, [query.data]);

  const salvar = trpc.settings.updatePacientes.useMutation({
    onSuccess: () => {
      toast.success("Configurações salvas");
      utils.settings.getPacientes.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao salvar"),
  });
  // Igual ao padrão grava `null`: a organização volta a seguir o padrão, inclusive se ele mudar.
  const handleSave = () => salvar.mutate(igualAoPadrao(valor) ? null : valor);
  const handleReset = () => {
    setValor(PADRAO);
    toast.info("Padrão carregado (salve para valer)");
  };
  const mensagemSemLink = !valor.anamnese.mensagem.includes("{link}");

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className={`${embutido ? "" : "mx-auto max-w-6xl "}w-full space-y-4 pb-24`}>
      {!embutido && (
        <Link
          to="/configuracoes"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Configurações
        </Link>
      )}

      {compacto ? <MobileTabs section={section} onChange={setSection} /> : null}

      <div className="flex gap-6">
        {!compacto && <DesktopSidebar section={section} onChange={setSection} />}

        <div className="min-w-0 flex-1">
          <div className="rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
            <div className="flex items-center gap-3 border-b border-zinc-100 px-6 py-4 dark:border-border">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: `${active.accent}1a`, color: active.accent }}>
                {active.icon}
              </div>
              <div>
                <h2 className="text-sm font-semibold text-zinc-900 dark:text-foreground">{active.label}</h2>
                <p className="text-xs text-zinc-500 dark:text-muted-foreground">{active.description}</p>
              </div>
            </div>
            <div className="px-4 py-5 sm:px-6">
              {section === "cadastro" && <SecaoCadastro valor={valor} onChange={setValor} />}
              {section === "anamnese" && <SecaoAnamnese valor={valor} onChange={setValor} />}
              {section === "ficha" && <SecaoFicha valor={valor} onChange={setValor} />}
            </div>
          </div>
        </div>
      </div>

      <div
        ref={ocupaRodape}
        // No celular fica em cima da barra de abas, que já cobre a área do gesto.
        className="fixed bottom-0 left-0 right-0 z-30 border-t border-zinc-200 bg-white pb-[env(safe-area-inset-bottom)] max-md:bottom-[calc(3.75rem+env(safe-area-inset-bottom))] max-md:pb-0 dark:border-border dark:bg-card"
      >
        <div className="mx-auto flex w-full max-w-6xl items-center justify-end gap-2 px-4 py-3 sm:px-6 md:px-8">
          <button
            type="button"
            onClick={handleReset}
            disabled={salvar.isPending}
            className="inline-flex h-10 items-center rounded-md border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-700 transition-colors hover:border-zinc-300 hover:bg-zinc-50 disabled:opacity-60 sm:h-9 dark:border-border dark:bg-card dark:text-foreground"
          >
            Restaurar padrão
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={salvar.isPending || mensagemSemLink}
            className="inline-flex h-10 items-center gap-1.5 rounded-md bg-brand-600 px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60 sm:h-9 sm:px-4 sm:font-medium"
          >
            {salvar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {salvar.isPending ? "Salvando..." : "Salvar alterações"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sidebar desktop (mesmo padrão de SettingsAgenda) ─────

function DesktopSidebar({ section, onChange }: { section: SectionKey; onChange: (k: SectionKey) => void }) {
  return (
    <aside className="w-60 shrink-0">
      <nav className="sticky top-20 space-y-1">
        {SECTIONS.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${
                active
                  ? "bg-zinc-50 text-zinc-900 dark:bg-muted/40 dark:text-foreground"
                  : "text-zinc-700 hover:bg-zinc-50 dark:text-foreground/80 dark:hover:bg-muted"
              }`}
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-transform group-hover:scale-105"
                style={{ backgroundColor: `${s.accent}1a`, color: s.accent }}
              >
                {s.icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{s.label}</p>
                <p className="truncate text-[11px] text-zinc-500 group-hover:text-zinc-600 dark:text-muted-foreground">{s.description}</p>
              </div>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

// ─── Tabs horizontais mobile (mesmo padrão de SettingsAgenda) ─

function MobileTabs({ section, onChange }: { section: SectionKey; onChange: (k: SectionKey) => void }) {
  return (
    <div className="-mx-4 overflow-x-auto border-b border-zinc-200 bg-white sm:-mx-6 dark:border-border dark:bg-card">
      <div className="flex gap-1 px-4 sm:px-6">
        {SECTIONS.map((s) => {
          const active = section === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onChange(s.key)}
              className={`inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-medium transition-colors ${
                active
                  ? "border-brand-600 text-brand-700 dark:border-primary dark:text-primary"
                  : "border-transparent text-zinc-600 hover:text-zinc-900 dark:text-foreground/70 dark:hover:text-foreground"
              }`}
            >
              {s.icon}
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
